import { SelectionModel } from '@angular/cdk/collections';
import { Component, ElementRef, HostBinding, inject, OnDestroy, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { GenericApiResponseError } from '@core/generic-api/data-access';
import { Helper } from '@core/util';
import { ToastService } from '@shared/components/toast';
import * as moment from 'moment';
import { DurationInputArg2 } from 'moment';
import { catchError, forkJoin, Observable, of, Subject, takeUntil, tap, throwError } from 'rxjs';
import { ParticipantApiService } from '../api/participant-api.service';
import { DocumentModel } from '../models/document.model';
import { FileUploaderModel } from '../models/file-uploader.model';
import { ParticipantModel } from '../models/participant.model';
import { ContentBuilderService } from '../services/content-builder.service';
import { DocumentService } from '../services/document.service';
import { ConfirmSubmitBottomSheetComponent } from './confirm-submit-bottom-sheet/confirm-submit-bottom-sheet.component';
import { DOCUMENT_STATUS_KEY, SigningType, TokenInfo, TokenStatus } from './document-signing.type';
import { PdfViewerComponent } from '@core/pdf-viewer/feature';
import { SignatureBoxComponent } from './signature-box/signature-box.component';
import { SigningOnPdfComponent } from './signing-on-pdf/signing-on-pdf.component';
import { SignatureBoxData } from './signing-on-pdf/signing-on-pdf.type';
import { VerifyCodeModalComponent } from './verify-code-modal/verify-code-modal.component';
import { CALLBACK_URL } from '../constants/document-signing.constant';

@Component({
  selector: 'portal-signing-document-signing',
  templateUrl: './document-signing.component.html',
  styleUrls: ['./document-signing.component.scss'],
})
export class DocumentSigningComponent implements OnInit, OnDestroy {
  private readonly callbackUrl: string[] | null = inject(CALLBACK_URL, { optional: true });
  private readonly SIGNING_MODE: string = 'html';
  private readonly activatedRoute = inject(ActivatedRoute);
  protected config: { menu: { hide: boolean }; utm_source?: string } | null;
  @ViewChild(PdfViewerComponent) pdfViewerComponent: any;
  @ViewChild('signingOnPdf') signingOnPdf!: SigningOnPdfComponent;
  @ViewChildren(SignatureBoxComponent) signatureBoxesComponent: any;
  @ViewChildren(SignatureBoxComponent, { read: ElementRef }) signatureBoxesRef: any;
  accessToken!: string;
  document!: DocumentModel;
  accessTokenInfo!: TokenInfo;
  scale = 1;
  signatureData: SignatureBoxData[] = [];
  validToken = false;
  tokenStatus!: TokenStatus;
  participant!: ParticipantModel;
  loading = false;
  formatDatetime = 'hh:mm A - DD MMM, YYYY';
  signingMode!: 'pdf' | 'html';
  signingTypes: SigningType[] = ['drawSignature'];
  isMobileMode = false;
  needVerifyOTP = true;
  invalidContract = false;
  roleWithoutAction = ['reviewer'];
  // Layout
  labelSubmitBtn = '';
  canShowSubmitBtn = false;
  canShowRejectBtn = false;
  canShowDownloadBtn: 'toolbar' | 'menu' | 'none' = 'none';
  // btn submit mobile
  mobileBtnState: 'show' | 'hidden' = 'hidden';
  titleConfirm = '';
  messageConfirm = '';
  // Upload
  messageUploadFile = 'Support files under 5MB.';
  maxFiles = 5;
  maxCurrentSlot = 5;
  maxFileSize = this.maxFiles * 1024 * 1024;
  allowedMimeTypes = [
    'image/jpg',
    'image/jpeg',
    'image/png',
    'image/bmp',
    'image/gif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/csv',
    'text/plain',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];
  allowExtensions = ['png', 'bmp', 'gif', 'jpeg', 'jpg', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv'];
  uploadSelection = new SelectionModel<FileUploaderModel>(true);
  /* FeedBack */
  feedbackForm!: FormGroup;
  hasSentFeedback = false;
  unsubscribeAll: Subject<void> = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    private toastService: ToastService,
    private participantApiService: ParticipantApiService,
    private contentBuilderService: ContentBuilderService,
    private documentService: DocumentService,
    private fb: FormBuilder,
  ) {
    try {
      this.config = JSON.parse(this.route.snapshot.queryParams['config']) ?? null;
    } catch (error) {
      this.config = null;
    }
  }

  @HostBinding('class') private get hostClass(): string {
    return this.config?.utm_source || '';
  }

  ngOnInit() {
    this.accessToken = this.route.snapshot.queryParams['accessToken'];
    this.accessTokenInfo = Helper.decodeJWT(this.accessToken) as any;
    this.setupLayout();
    // await this.loadContractDetail();
    this.participantApiService
      .getContractDetail(this.accessToken)
      .pipe(
        tap(doc => {
          this.document = doc;
          const participant = this.document.participants.find(i => i.markupId === this.accessTokenInfo.uuid);
          if (participant) {
            this.participant = participant;
          }
          // cheat if participant dont have any signature box, force disable singpass
          // const hasSignatureBox = Object.values(this.document.fields.properties).some(property => {
          //     return property.config?.markupId === this.accessTokenInfo.uuid && property.fieldType === 'signatureField';
          // });
          // if (this.document.metadata.eSigning) {
          //     this.document.metadata.eSigning.enableESigning = !!hasSignatureBox;
          // }
        }),
        catchError(error => {
          /* Mark contract invalid */
          this.invalidContract = true;

          return throwError(error);
        }),
        takeUntil(this.unsubscribeAll),
      )
      .subscribe(() => {
        this.checkTokenStatus();
        this.initMode();

        if (!this.validToken && this.tokenStatus === 'confirmed' && this.accessTokenInfo.action === 'signer') {
          this.router.navigate(['view'], {
            relativeTo: this.activatedRoute,
            queryParams: { accessToken: this.accessToken },
            replaceUrl: true,
          });
        }

        // Tắt function OTP
        this.needVerifyOTP = false;
      });
    // this.verifyCode();
    this.participantApiService
      .postManualEdit(
        {
          name: 'view',
          description: `The ${this.accessTokenInfo.action} views document`,
          metadata: {
            anyOtherInfo: this.accessTokenInfo,
          },
        },
        this.accessToken,
      )
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
  }

  setupLayout(): void {
    if (this.accessTokenInfo.role === 'view-only') {
      this.canShowRejectBtn = false;
      this.canShowSubmitBtn = false;
      return;
    }
    switch (this.accessTokenInfo.action) {
      case 'reviewer':
        this.labelSubmitBtn = 'i agree';
        this.canShowRejectBtn = true;
        this.canShowSubmitBtn = true;
        this.canShowDownloadBtn = 'toolbar';
        this.titleConfirm = 'Thank you!';
        this.messageConfirm =
          'A confirmation email will be sent to you shortly. You will also be notified when all parties have signed the document.';
        break;
      case 'approver':
        this.labelSubmitBtn = 'accept';
        this.canShowRejectBtn = true;
        this.canShowSubmitBtn = true;
        this.canShowDownloadBtn = 'menu';
        this.titleConfirm = 'Thank you!';
        this.messageConfirm =
          'A confirmation email will be sent to you shortly. You will also be notified when all parties have signed the document.';
        break;
      case 'signer':
        this.labelSubmitBtn = 'submit document';
        this.canShowRejectBtn = true;
        this.canShowSubmitBtn = true;
        this.canShowDownloadBtn = 'menu';
        this.titleConfirm = 'You have successfully signed the document!';
        this.messageConfirm = 'A confirmation email will be sent to you shortly.';
        break;
      case 'forwarder':
        this.labelSubmitBtn = 'forward';
        this.canShowRejectBtn = true;
        this.canShowSubmitBtn = true;
        this.canShowDownloadBtn = 'menu';
        this.titleConfirm = 'Thank you!';
        this.messageConfirm =
          'A confirmation email will be sent to you shortly. You will also be notified when all parties have signed the document.';
        break;
    }
  }

  initMode(): void {
    const hasBoxAction = Object.values(this.document.fields.properties).some(property => {
      return property.config?.markupId === this.accessTokenInfo.uuid;
    });
    if (this.accessTokenInfo.action === 'forwarder') {
      this.signingMode = 'html';
    } else if (this.roleWithoutAction.includes(this.accessTokenInfo.action) || this.SIGNING_MODE === 'pdf' || !hasBoxAction) {
      this.signingMode = 'pdf';
    } else {
      this.signingMode = 'html';
    }
    if (this.document.metadata?.eSigning && this.document.metadata?.eSigning.enableESigning) {
      this.enableSingpass();
    }
    const signingModeInput = this.route.snapshot.queryParams['signingMode'];
    if (signingModeInput) {
      this.signingMode = signingModeInput;
    }
  }

  // async loadContractDetail() {
  //     this.document = await this.participantApiService.getContractDetail(this.accessToken).toPromise();
  //     const hasBoxAction = Object.values(this.document.fields.properties).some(property => {
  //         return property.config?.markupId === this.accessTokenInfo.uuid;
  //     });
  //     if (this.roleWithoutAction.includes(this.accessTokenInfo.action) || this.SIGNING_MODE === 'pdf' || !hasBoxAction) {
  //         this.signingMode = 'pdf';
  //     } else {
  //         this.signingMode = 'html';
  //     }
  //     if (this.document.metadata?.eSigning && this.document.metadata?.eSigning.enableESigning) {
  //         this.enableSingpass();
  //     }
  //     const signingModeInput = this.route.snapshot.queryParams['signingMode'];
  //     if (signingModeInput) {
  //         this.signingMode = signingModeInput;
  //     }
  //     this.checkTokenStatus();
  // }

  enableSingpass() {
    this.signingTypes = ['singpass'];
    this.signingMode = 'pdf';
  }

  checkTokenStatus() {
    if (this.accessTokenInfo.issuerName === 'SharingTokenIssuer') {
      this.validToken = false;
      this.tokenStatus = 'readonly';
      return;
    }

    // enable singpass for participant
    if (this.participant?.metadata && this.participant?.metadata?.eSigning && this.participant.metadata.eSigning.enableESigning) {
      this.enableSingpass();
    }

    this.tokenStatus = this.participant.status as TokenStatus;
    this.validToken = true;

    if (this.participant.status === 'confirmed') {
      if (this.roleWithoutAction.includes(this.accessTokenInfo.action)) {
        this.validToken = false;
        this.tokenStatus = 'readonly';
      } else {
        this.validToken = false;
        this.tokenStatus = 'confirmed';
      }
    } else if (this.document.status === DOCUMENT_STATUS_KEY.CANCELED) {
      this.validToken = false;
      this.tokenStatus = 'rejected';
    } else if (this.document.status === DOCUMENT_STATUS_KEY.ABORTED) {
      this.validToken = false;
      this.tokenStatus = 'aborted';
    } else {
      if (this.document.metadata && this.document.metadata.expirationSetting && this.document.metadata.expirationSetting.enableExpiration) {
        const startDate = moment(this.document.metadata.expirationSetting.startDate);
        const endDate = startDate
          .add(
            this.document.metadata.expirationSetting.expireAfter,
            (this.document.metadata.expirationSetting.expireAfterUnit as DurationInputArg2) || 'days',
          )
          .endOf('day');
        if (endDate < moment()) {
          this.validToken = false;
          this.tokenStatus = 'expired';
        }
      }

      // https://redreborn.atlassian.net/jira/software/projects/CIMB/boards/27?assignee=5d84419ab29eab0c3cbac4ed&selectedIssue=CIMB-750
      if (
        this.document.metadata.caseInfo &&
        this.document.metadata.caseInfo.type === 'letterOffer' &&
        this.document.metadata.signingExpiration &&
        moment(this.document.metadata.signingExpiration).valueOf() < moment().valueOf()
      ) {
        this.validToken = false;
        this.tokenStatus = 'expired';
      }
    }
  }

  // submit(code?: any) {
  //     let data: { [x in string]: any } = null as any;
  //     switch (this.accessTokenInfo.action) {
  //         case 'approver':
  //         case 'signer':
  //             data = this.getPayloadFromSigner();
  //             break;

  //         case 'forwarder':
  //             data = this.getPayloadFromForwarder();
  //             break;
  //     }
  //     if (!data) {
  //         return;
  //     }
  //     if (code) {
  //         data['otpCode'] = code;
  //     }
  //     const dialog = this.dialog.open(ConfirmSubmitDialogComponent, {
  //         width: '100%',
  //         maxWidth: '480px',
  //         data: {
  //             attachments: this.uploadSelection.selected
  //         }
  //     });
  //     dialog.afterClosed().subscribe(confirm => {
  //         if (!confirm) {
  //             return;
  //         }
  //         this.sendRequest(data);
  //     });
  // }

  submit(code?: any) {
    let data: { [x in string]: any } = null as any;
    switch (this.accessTokenInfo.action) {
      case 'approver':
      case 'signer':
        data = this.getPayloadFromSigner();
        break;

      // case 'forwarder':
      //   data = this.getPayloadFromForwarder();
      //   break;
    }
    if (!data) {
      return;
    }
    if (code) {
      data['otpCode'] = code;
    }
    this.sendRequest(data);
  }

  sendRequest(data: { [x in string]: any }): void {
    this.participantApiService
      .postParticipantAction(
        {
          action: 'confirmed',
          data,
          clientActionAt: moment(),
        },
        this.accessToken,
      )
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe({
        next: () => {
          if (Array.isArray(this.callbackUrl) && this.callbackUrl.length > 0) {
            this.router.navigate(this.callbackUrl, { queryParams: { accessToken: this.accessToken } });
            // this.router.navigate(this.callbackUrl, { replaceUrl: true });
            return;
          }

          this.toastService.notify({ type: 'success', messages: 'Your signature was submitted successfully!' });
          this.validToken = false;
          this.tokenStatus = 'justSigned';
        },
        error: (error: GenericApiResponseError) => {
          if (error.errorCode === 'CTM008') {
            this.toastService.notify({ type: 'warning', messages: 'This document is completed' });
            return;
          }

          if (error.errorCode === 'CTM009') {
            this.toastService.notify({
              type: 'danger',
              messages: 'This action is no longer needed, please refresh the document for latest update',
            });
            return;
          }
        },
      });
  }

  // updateToLocalStorage() {
  //     const contracts: IContract[] = LocalStorageService.getItem(AppConstant.LOCAL_STORAGE_KEYS.CONTRACTS);
  //     if (contracts) {
  //         const contract = contracts.find(c => c.accessToken === this.accessToken);
  //         if (contract) {
  //             contract.contract.status = 'completed';
  //             const index = contracts.findIndex(c => c.accessToken === this.accessToken);
  //             contracts[index] = contract;
  //             LocalStorageService.setItem(AppConstant.LOCAL_STORAGE_KEYS.CONTRACTS, contracts);
  //         }
  //     }
  // }

  // sendRequest(data: { [x in string]: any }): void {
  //     // this.globalService.loader.loading();
  //     this.attachFiles(this.uploadSelection.selected).pipe(
  //         concatMap(() => this.participantApiService.postParticipantAction({
  //             action: 'confirmed',
  //             data: data,
  //             clientActionAt: moment()
  //         }, this.accessToken))
  //         // finalize(() => {
  //         //     this.globalService.loader.loaded();
  //         // })
  //     ).subscribe(res => {
  //         this.toastService.notify({ type: 'success', messages: 'Your signature was submitted successfully!' });
  //         this.validToken = false;
  //         this.tokenStatus = 'justSigned';

  //         /* Init feedback form */
  //         this.initFeedbackForm();
  //     }, (error: IErrorResponse) => {
  //         /** useless for current business */
  //         // if (error.error && error.error.data && error.error.data.otpCode) {
  //         //     if (error.error.data.otpCode.inValid) {
  //         //         this.verifyCode(error.error.data.otpCode);
  //         //     } else if (error.error.data.otpCode.serviceErrorCode === 60202) {
  //         //         this.globalService.message.next({
  //         //             type: MESSAGE_TYPE.error,
  //         //             message: this.translateService.instant('document.signing.otp_reached_max')
  //         //         });
  //         //     }
  //         //     return;
  //         // }
  //         /** */
  //         if (error.errorCode === 'CTM008') {
  //             this.toastService.notify({ type: 'warning', messages: 'This document is completed' });
  //             return;
  //         }

  //         if (error.errorCode === 'CTM009') {
  //             this.toastService.notify({ type: 'danger', messages: 'This action is no longer needed, please refresh the document for latest update' });
  //             return;
  //         }
  //     });
  // }

  getPayloadFromSigner(): any {
    const data: { [x in string]: any } = {};
    // if (this.signingMode === 'html') {
    //   if (!this.signingOnHtml.validate()) {
    //     this.toastService.notify({ type: 'danger', messages: 'Please complete all fields in view!' });
    //     this.signingOnHtml.scrollFieldInvalid();
    //     return;
    //   }
    //
    //   const dataRaw = this.signingOnHtml.getPayload();
    //   this.signatureData = dataRaw['signatures'];
    //   data = dataRaw;
    // } else
    if (this.signingMode === 'pdf' && this.accessTokenInfo.action === 'signer') {
      // noti for singpass case
      if (!this.signatureData.length) {
        this.toastService.notify({ type: 'danger', messages: 'Please complete all fields in view!' });
      }
    }
    const count = this.contentBuilderService.countSignatureFieldByMarkupId(this.document, this.accessTokenInfo.uuid);
    if (this.signatureData && this.signatureData.length === count) {
      // Encode each signature item as base64 (JSON string -> base64) before sending
      // If encoding fails, let the error propagate so the caller can handle it and stop the submit flow.
      data['signatures'] = this.signatureData.map(item => {
        if (typeof item.signature !== 'string') throw new Error('Invalid signature data');
        const signature = Helper.encodeToBase64(item.signature);
        return { ...item, signature };
      });
      return data;
    }
    return null;
  }

  // getPayloadFromForwarder(): any {
  //   const data: { [x in string]: any } = {};
  //   if (this.signingOnHtml && !this.signingOnHtml.validate()) {
  //     this.toastService.notify({ type: 'danger', messages: 'Please complete all fields in view!' });
  //     this.signingOnHtml.scrollFieldInvalid();
  //     return;
  //   }
  //   data['participants'] = this.signingOnHtml.getPayloadForForwarder();
  //   return data;
  // }

  download() {
    this.participantApiService
      .getContractHtml(this.accessToken)
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe(html => {
        const printOptions = {
          margin: this.document.content.printOptions.margin,
          headerTemplate: this.document.header,
          footerTemplate: this.document.footer,
          displayHeaderFooter: true,
          isHideBottomLineSignature: true,
        };
        this.documentService.download(html, this.document.name, printOptions, this.accessToken);
        this.participantApiService
          .postManualEdit(
            {
              name: 'download',
              description: 'The Signer downloads document',
              metadata: {
                anyOtherInfo: this.accessTokenInfo,
              },
            },
            this.accessToken,
          )
          .pipe(takeUntil(this.unsubscribeAll))
          .subscribe();
      });
  }

  // decline() {
  //     const dialogRef = this.dialog.open(SigningDeclineModalComponent, {
  //         width: '460px',
  //         maxHeight: '600px',
  //         autoFocus: false,
  //         panelClass: 'signing-dialog'
  //     });
  //     dialogRef.afterClosed().subscribe((result: { isSubmit: boolean, reason: string; }) => {
  //         if (result && result.isSubmit) {
  //             this.participantApiService.postParticipantAction({
  //                 action: 'rejected',
  //                 data: {
  //                     reason: result.reason
  //                 },
  //                 clientActionAt: moment()
  //             }, this.accessToken).subscribe(res => {
  //                 this.validToken = false;
  //                 this.tokenStatus = 'rejected';
  //             });
  //         }
  //     });
  // }

  approveContent(): void {
    this.participantApiService
      .postParticipantAction(
        {
          action: 'confirmed',
          clientActionAt: moment(),
        },
        this.accessToken,
      )
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe(res => {
        this.validToken = false;
        this.tokenStatus = 'confirmed';
      });
  }

  verifyCode() {
    if (this.participant.metadata.requireOTP) {
      const config: MatDialogConfig = {
        width: '460px',
        maxHeight: '600px',
        disableClose: true,
        autoFocus: false,
        data: {
          accessToken: this.accessToken,
          phone: this.participant.phone,
        },
      };
      const dialogRef = this.dialog.open(VerifyCodeModalComponent, config);
      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.unsubscribeAll))
        .subscribe((result: { isSubmit: boolean; code: string }) => {
          if (result && result.isSubmit) {
            this.needVerifyOTP = false;
          }
        });
    } else {
      this.needVerifyOTP = false;
    }
  }

  pdfSigningCompleted(data: any) {
    this.signatureData = data;

    // When sign by singpass will submit document after sign
    if (this.signingTypes.includes('singpass')) {
      this.submit();
    }
  }

  openAttachmentBottomSheet(): void {
    const bottomsheet = this.bottomSheet.open(ConfirmSubmitBottomSheetComponent, {
      panelClass: 'attachment-bottom-sheet',
      data: {
        attachments: this.uploadSelection.selected,
        accessToken: this.accessToken,
      },
    });
    bottomsheet
      .afterDismissed()
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe(attachments => {
        if (!attachments) {
          return;
        }
        this.uploadSelection.select(...attachments);
      });
  }

  getUploadLink(data: { type: any; name: any; acl?: any }): Observable<any> {
    return this.participantApiService.getUploadLink(this.accessToken, data);
  }

  attachFiles(files: FileUploaderModel[]): Observable<any> {
    const requests: any = [];
    files.forEach(file => {
      requests.push(
        this.participantApiService.attachFile(this.accessToken, {
          name: file.file.name,
          contentType: file.file.type,
          keyPath: file.key,
        }),
      );
    });
    return requests.length > 0 ? forkJoin(requests) : of({});
  }

  onAddPhoto(data: FileUploaderModel): void {
    if (data.error?.status) {
      this.toastService.notify({ type: 'danger', messages: data.error.message ?? '' });
    } else {
      this.uploadSelection.select(data);
      this.calculateAvailableSlot();
    }
  }

  onRemovePhotos(data: FileUploaderModel[]): void {
    for (let i = 0; i < data.length; i++) {
      this.onRemovePhoto(data[i]);
    }
  }

  onRemovePhoto(data: FileUploaderModel): void {
    this.uploadSelection.deselect(data);
    this.calculateAvailableSlot();
  }

  calculateAvailableSlot(): void {
    this.maxCurrentSlot = this.maxFiles - this.uploadSelection.selected.length;
  }

  comfirmFullSlot(): void {
    const msg = `Maximum ${this.maxFiles} file(s)`;
    // this.isUpLoading = false;
    this.toastService.notify({ type: 'danger', messages: msg });
  }

  submitFeedback() {
    this.feedbackForm.markAllAsTouched();

    if (this.feedbackForm.invalid) {
      return;
    }

    const feedback = this.feedbackForm.getRawValue();

    this.participantApiService.sendFeedbackToAdmin(feedback, this.accessToken).subscribe(() => {
      this.toastService.notify({ type: 'success', messages: 'Thank you for your feedback!' });
      this.hasSentFeedback = true;
    });
  }

  onMobileSubmitBtnStateChange(state: 'show' | 'hidden'): void {
    this.mobileBtnState = state;
  }

  private initFeedbackForm() {
    this.feedbackForm = this.fb.group({
      ratingScore: [0, []],
      comment: ['', []],
    });
  }
}
